import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import { can as canDo, gateMessage, isLoginOnly } from "constants/permissions";

/**
 * 회원 유형 기반 권한 게이팅 훅.
 *
 *   const { can, gate, notice, closeNotice } = usePermission();
 *   <button disabled={!can("sub.apply")} onClick={() => gate("sub.apply", doApply)}>지원</button>
 *   <RoleNoticeModal notice={notice} onClose={closeNotice} />
 *
 * 동작:
 *  - 허용 → onAllowed 실행
 *  - 로그인만 필요한 액션인데 비회원 → 로그인 화면으로 이동(원래 자리 redirect)
 *  - 유형 제한 액션인데 비회원/유형부족 → 안내 모달(notice) 노출 (기획: 안내 문구 표시)
 */
export default function usePermission() {
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notice, setNotice] = useState(null); // { message, loggedIn } | null

  const can = (action) => canDo(user, action);

  const goLogin = () => {
    const redirect = encodeURIComponent(location.pathname + location.search);
    navigate(`/login?redirect=${redirect}`);
  };

  const gate = (action, onAllowed) => {
    if (can(action)) {
      onAllowed?.();
      return true;
    }
    // 로그인만 필요한 액션(공간 문의·커뮤니티 글쓰기 등) → 바로 로그인
    if (isLoginOnly(action)) {
      goLogin();
      return false;
    }
    // 유형 제한 액션 → 안내 모달 (비회원이면 로그인 CTA, 로그인이면 전환 CTA)
    setNotice({ message: gateMessage(action), loggedIn: isLoggedIn, action });
    return false;
  };

  return { can, gate, notice, closeNotice: () => setNotice(null), goLogin, user };
}
